'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Fragment, useEffect, useRef, useState } from 'react'
import { OrgInfo } from '../OrgInfo'
import { Search } from 'lucide-react'

import { getOrganizationsAction } from '@/app/actions/organization'
import { Drawer } from '@/components/Drawer'
import { OrgCard } from '@/components/OrgCard'
import { OrganizationsGridSkeleton } from '@/components/OrgCard/skeleton/organizations-grid-skeleton'
import OrgForm from '@/components/OrgForm'
import { RevealFade, RevealP, RevealSection } from '@/components/RevealSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { useDebounce } from '@/hooks/useDebounce'
import { Organization, User } from '@/payload-types'
import { useLenisContext } from '@/providers/Lenis'

import type { OrgParamsType } from '@/blocks/RenderOrganizationBlocks'

const DEFAULT_ITEMS_PER_PAGE = 9

export type SearchableOrganizationsProps = {
  params: OrgParamsType & {
    organizations: { data: Organization[]; totalDocs: number }
    activeOrg: Organization | null
  }
  payloadUser: User | null
}

export const SearchableOrganizations: React.FC<SearchableOrganizationsProps> = ({
  payloadUser,
  params: {
    organizations: serverOrganizations,
    activeOrg: serverActiveOrganization,
    addOrg: serverAddOrg,
    pageNum: serverPageNum,
    search: serverSearch,
  },
}) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const currentParams = useRef(searchParams.toString())

  const [organizations, setOrganizations] = useState<Organization[]>(serverOrganizations.data)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalDocs, setTotalDocs] = useState(serverOrganizations.totalDocs)
  const [searchTerm, setSearchTerm] = useState(serverSearch ?? '')
  const [activeOrganization, setActiveOrganization] = useState<Organization | null>(
    serverActiveOrganization,
  )
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const [open, setOpen] = useState(serverActiveOrganization ? true : false)
  // Initialize once from the server prop / URL; afterwards the drawer state is
  // the source of truth and the `add` param is only written (via updateParam),
  // never synced back, since router.push updates searchParams asynchronously.
  const [addOrgOpen, setAddOrgOpen] = useState(
    () => Boolean(serverAddOrg) || searchParams.get('add') === 'true',
  )
  const [currentPage, setCurrentPage] = useState(serverPageNum ? Number(serverPageNum) : 1)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const otherOrgsRef = useRef<HTMLDivElement>(null)
  const otherOrgsGridRef = useRef<HTMLDivElement>(null)
  const { lenis } = useLenisContext()

  // Keep the ref used by updateParam in sync with the URL as navigation settles
  useEffect(() => {
    currentParams.current = searchParams.toString()
  }, [searchParams])

  const updateParam = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(currentParams.current)

    if (newParams.get(key) === value) {
      return
    }

    if (value === null) {
      newParams.delete(key)
    } else {
      newParams.set(key, value)
    }

    currentParams.current = newParams.toString()
    router.push(`${pathname}?${currentParams.current}`, { scroll: false })
  }

  const fetchOrganizations = async (params?: {
    page?: string | number
    search?: string
    sort?: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      const organizations = await getOrganizationsAction({
        limit: DEFAULT_ITEMS_PER_PAGE,
        page: params?.page ?? currentPage,
        search: (params?.search ?? debouncedSearchTerm).trim(),
        sort: params?.sort ?? '_order',
      })

      if (organizations === null) {
        throw new Error('Failed to load organizations')
      }

      setOrganizations(organizations.data)
      setTotalDocs(organizations.totalDocs)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations')
      setOrganizations([])
      setTotalDocs(0)
    } finally {
      setLoading(false)
    }
  }

  const handleActiveOrg = (organization: Organization | null) => {
    if (organization === null) {
      setActiveOrganization(null)
      setOpen(false)
      updateParam('org', null)
      return
    }

    updateParam('org', organization.slug)
    setOpen(true)
    setActiveOrganization(organization)
  }

  const handlePageChange = (newPage: number) => {
    updateParam('p', newPage === 1 ? null : String(newPage))
    setCurrentPage(newPage)
    lenis?.scrollTo(otherOrgsRef.current as HTMLElement, { offset: -84 })
    otherOrgsGridRef.current?.focus()
    fetchOrganizations({ page: newPage })
  }

  const handleAddOrg = () => {
    updateParam('add', 'true')
    setAddOrgOpen(true)
  }

  const handleSearchChange = (value: string) => {
    updateParam('q', value.trim().length > 0 ? value.trim() : null)
    setSearchTerm(value)
    fetchOrganizations({ search: value.trim() })
  }

  const handleDrawerChange = (newOpen: boolean) => {
    if (!newOpen) {
      handleActiveOrg(null)
    }
  }

  const handleAddOrgOpen = (newOpen: boolean) => {
    if (!newOpen) {
      updateParam('add', null)
      setAddOrgOpen(false)
    } else {
      updateParam('add', 'true')
      setAddOrgOpen(true)
    }
  }

  return (
    <div ref={otherOrgsRef} className="wrapper">
      <RevealSection
        stagger
        threshold={0.005}
        className="flex flex-wrap items-center justify-between gap-4 pb-16"
      >
        <h2 className="font-family-body text-3xl font-semibold text-card-foreground">
          Organizations
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search by name, issues, or keyword..."
            IconComponent={Search}
            className="min-w-75 max-xs:w-full"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onClear={() => handleSearchChange('')}
            autoComplete="none"
            aria-label="Search for an organization"
          />
          <Button variant="accent" onClick={handleAddOrg} className="max-xs:w-full">
            Add organization
          </Button>
        </div>
      </RevealSection>
      {loading ? (
        <RevealSection className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <OrganizationsGridSkeleton />
        </RevealSection>
      ) : error ? (
        <RevealFade className="flex h-full items-center justify-center">
          <p className="text-red-500">
            There was an error loading organizations, please try again later.
          </p>
        </RevealFade>
      ) : organizations.length > 0 ? (
        <>
          <RevealSection
            stagger
            threshold={0.001}
            ref={otherOrgsGridRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
          >
            {organizations.map((organization) => (
              <Fragment key={organization.id}>
                <OrgCard
                  doc={organization}
                  relationTo="organizations"
                  onClick={() => handleActiveOrg(organization)}
                />
              </Fragment>
            ))}
          </RevealSection>
          <RevealSection>
            <Pagination
              numResults={totalDocs}
              resultsPerPage={DEFAULT_ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              className="my-12"
            />
          </RevealSection>
        </>
      ) : (
        <div className="py-12 text-center font-body text-card-foreground">
          <RevealP className="font-body text-lg font-medium">
            No organizations found {searchTerm.trim() ? `matching "${searchTerm}"` : ''}
          </RevealP>
        </div>
      )}
      {activeOrganization && !addOrgOpen && (
        <Drawer open={open} setOpen={handleDrawerChange} ariaTitle={activeOrganization.title}>
          <OrgInfo organization={activeOrganization} />
        </Drawer>
      )}
      <Drawer open={addOrgOpen} setOpen={handleAddOrgOpen} ariaTitle="Add Organization">
        <OrgForm payloadUser={payloadUser} />
      </Drawer>
    </div>
  )
}
